import React, { useEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { FiMoreVertical } from "react-icons/fi";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DonutWheelIcon from "./DonutWheelIcon";
import { colors } from "../../../utils";
import { captureTypeLabel } from "../Capture.helpers";
import { ATTACH_DETACH_WHEELS_LABEL } from "../captureLabels";
import { MenuAnchor, Menu, MenuItemBtn, MenuKebabBtn } from "../styles";

/**
 * BUG-capture-8b690e (Gordon approved, 2026-08-13, DECISIONS-RESOLVED.md:
 * "Capture 3-dot (8b690e): YES — collapse Capture row actions into a ⋯
 * overflow menu. Build.") — consolidates the row's separate Detach / Edit /
 * Add-to-Wheel / Delete icon buttons into a single 3-dot (kebab) overflow
 * menu, matching the app's existing overflow-menu pattern (Chat's
 * ConversationRow.kebabMenu / ThreadMenu: a persistent trigger button with
 * aria-haspopup="menu" opening a role="menu" of role="menuitem" children,
 * same accessible-name on trigger and menu, closes on outside-click/Escape,
 * returns focus to the trigger on close).
 *
 * QA-78 (3) (Gordon, 2026-08-04) required the row's actions be PERSISTENT —
 * "always shown on each card, not hover/select-gated." That still holds: the
 * KEBAB TRIGGER itself is unconditionally rendered on every row (not
 * hover-gated), same as the discrete icons it replaces were. Only the
 * expanded menu of actions is click-gated now, which is the whole point of
 * an overflow menu — every action stays reachable, just behind one control
 * instead of three-to-four competing for row width.
 *
 * QA-81 (Gordon, 2026-08-04) fixed the action ORDER at edit-pen, wheel,
 * trash, with a conditional Detach leading when the row is attached. That
 * order is preserved inside the menu: Detach (if attached) -> Edit (if
 * editable) -> Manage Wheels -> Create new Wheel -> Delete. The old
 * two-action "Add to Wheel" popover (AddToWheelMenu.jsx) is flattened into
 * two direct menu items here rather than nested as a menu-inside-a-menu,
 * which is both simpler to operate and reads as "cleaner rows, modern
 * pattern" per the bug's own ask.
 */
const CaptureRowMenu = ({
  item,
  attached,
  editable,
  isDeleting,
  isDetaching,
  onEdit,
  onDelete,
  onDetach,
  onAttach,
  onCreateNew,
}) => {
  const [open, setOpen] = useState(false);
  const [tooltipHovered, setTooltipHovered] = useState(false);
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const typeLabel = captureTypeLabel(item.type);
  const triggerLabel = `Actions for ${typeLabel} capture`;

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (fn) => () => {
    setOpen(false);
    fn?.();
  };

  return (
    <MenuAnchor ref={anchorRef}>
      <Tooltip
        title="More actions"
        open={tooltipHovered && !open}
        onOpen={() => setTooltipHovered(true)}
        onClose={() => setTooltipHovered(false)}
      >
        <MenuKebabBtn
          ref={triggerRef}
          type="button"
          aria-label={triggerLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <FiMoreVertical aria-hidden="true" size={16} />
        </MenuKebabBtn>
      </Tooltip>

      {open && (
        <Menu role="menu" aria-label={triggerLabel}>
          {/* Temporarily hidden — detach from capture row menu
          {attached && (
            <MenuItemBtn
              role="menuitem"
              type="button"
              aria-label={`Detach ${typeLabel} capture from Wheel`}
              disabled={isDetaching}
              onClick={choose(() => onDetach?.(item))}
            >
              <LinkOffRoundedIcon fontSize="small" />
              Detach from Wheel
            </MenuItemBtn>
          )}
          */}
          {editable && (
            <MenuItemBtn
              role="menuitem"
              type="button"
              aria-label={`Edit ${typeLabel} capture`}
              onClick={choose(() => onEdit?.(item))}
            >
              <EditOutlinedIcon fontSize="small" />
              Edit
            </MenuItemBtn>
          )}
          <MenuItemBtn
            role="menuitem"
            type="button"
            aria-label={ATTACH_DETACH_WHEELS_LABEL}
            onClick={choose(() => onAttach(item))}
          >
            <DonutWheelIcon size={18} color={colors.PrimaryPurple600} />
            {ATTACH_DETACH_WHEELS_LABEL}
          </MenuItemBtn>
          <MenuItemBtn
            role="menuitem"
            type="button"
            aria-label="Create new Wheel"
            onClick={choose(() => onCreateNew?.(item))}
          >
            <AddRoundedIcon fontSize="small" />
            Create new Wheel
          </MenuItemBtn>
          <MenuItemBtn
            role="menuitem"
            type="button"
            aria-label="Delete capture"
            disabled={isDeleting}
            onClick={choose(() => onDelete(item))}
          >
            <DeleteOutlineRoundedIcon fontSize="small" />
            Delete
          </MenuItemBtn>
        </Menu>
      )}
    </MenuAnchor>
  );
};

export default CaptureRowMenu;
